import { Play as PlayIcon } from '@tunasong/icons'
import type { CadenceTag, Chord, ProgressionTag } from '@tunasong/music-lib'
import { ChordLib, isChordVariant } from '@tunasong/music-lib'
import type { NoteEvent } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { IconButton, Popup, Tooltip } from '@tunasong/ui-lib'
import type { FC, KeyboardEvent, MouseEvent } from 'react'
import { useRef, useState } from 'react'
import { usePlayNotes } from '../player/piano-player.hook.js'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { TheoryCard } from './theory-card.js'
import styles from './window-tag.module.css'

export interface WindowTagProps {
  cadence: CadenceTag | null
  progression: ProgressionTag | null
  /**
   * Chords backing the window. Used by the cadence-card "Hear it" button to
   * play the two chords of the cadence span. Indices in the cadence `span`
   * are relative to this array.
   */
  chords: Chord[]
}

const buildAriaLabel = (kind: 'cadence' | 'progression', displayName: string): string => {
  const noun = kind === 'cadence' ? 'Cadence' : 'Progression'
  return `${noun}: ${displayName}. Click to learn more.`
}

/**
 * Build the two-chord NoteEvent sequence the cadence "Hear it" button plays.
 * Root-position triad voicings, half-note durations (`'2n'`), octave 4 — same
 * primitives the chord popup uses. Skips `ChordNotes` inputs (notes-driven
 * chords don't carry a triad variant; their playback story is the chord
 * popup, not the cadence card).
 */
const buildCadenceEvents = (chords: Chord[]): NoteEvent[] => {
  const events: NoteEvent[] = []
  for (const chord of chords) {
    if (!isChordVariant(chord)) {
      continue
    }
    for (const note of ChordLib.getNotes(chord)) {
      events.push({
        type: 'note',
        note: `${note}4` as NoteName,
        duration: '2n',
      })
    }
  }
  return events
}

/**
 * End-of-line theory chip — renders the cadence or progression for a chord
 * line. Cadence wins over progression when both fire (Joni's §2.1 + Les's
 * spec §3.4: cadence is the more local, stronger pedagogical signal).
 *
 * Accessibility (Joni P0 escalations honored in this component so all future
 * surfaces inherit):
 *   - The tag is a real `<button>` with `tabIndex={0}` and an `aria-label`
 *     describing the cadence kind.
 *   - `:focus-visible` outline is the `--tuna-palette-primary-main` ring
 *     used elsewhere in the codebase.
 *   - The tag underline (CSS `text-decoration: underline dotted`) is a
 *     non-chromatic affordance — color-blind users see the same role.
 *   - Enter / Space activate (default `<button>` behavior).
 *   - Escape on the open card closes it (Popup's `closeOnEscape` default).
 *
 * Renders `null` when:
 *   - cadence and progression are both null,
 *   - `useTheoryCopy` returns null (theoryLevel = 'off' or missing entry).
 */
export const WindowTag: FC<WindowTagProps> = props => {
  const { cadence, progression, chords } = props
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const { playNoteSequence } = usePlayNotes()

  // Cadence wins over progression on collision (§2.1 / §3.4).
  const activeId = cadence?.lexiconId ?? progression?.lexiconId ?? ''
  const activeKind: 'cadence' | 'progression' | null = cadence ? 'cadence' : progression ? 'progression' : null
  const copy = useTheoryCopy(activeId)

  if (!activeKind || !copy) {
    return null
  }

  const handleToggle = (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault()
    ev.stopPropagation()
    setOpen(prev => !prev)
  }

  const handleKeyDown = (ev: KeyboardEvent<HTMLButtonElement>) => {
    if (ev.key === 'Escape' && open) {
      ev.preventDefault()
      setOpen(false)
    }
  }

  const handleClose = () => setOpen(false)

  const handlePlay = () => {
    if (!cadence) {
      return
    }
    const [a, b] = cadence.span
    const spanChords = [chords[a], chords[b]].filter((c): c is Chord => Boolean(c))
    const events = buildCadenceEvents(spanChords)
    if (events.length > 0) {
      playNoteSequence(events)
    }
  }

  const ariaLabel = buildAriaLabel(activeKind, copy.displayName)
  const tagLabel = copy.tag
  // Roman shorthand for the card heading (cadence kind → V → I, IV → I, etc.).
  // Only added at advanced level so the card stays prose-forward at beginner /
  // intermediate. Mapping is per cadence kind; no lexicon churn required.
  const cadenceShorthand: Record<CadenceTag['kind'], string> = {
    authentic: 'V → I',
    plagal: 'IV → I',
    half: '… → V',
    deceptive: 'V → vi',
  }
  const headingSuffix =
    activeKind === 'cadence' && cadence && copy.level === 'advanced'
      ? cadenceShorthand[cadence.kind]
      : undefined

  const action =
    activeKind === 'cadence' && cadence ? (
      <Tooltip title="Play cadence">
        <IconButton aria-label="Play cadence" onClick={handlePlay} size="small">
          <PlayIcon />
        </IconButton>
      </Tooltip>
    ) : undefined

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-expanded={open}
        className={styles.tag}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        {tagLabel}
      </button>
      <Popup open={open} anchorEl={anchorRef.current} onClose={handleClose}>
        <TheoryCard lexiconId={activeId} action={action} headingSuffix={headingSuffix} />
      </Popup>
    </>
  )
}
